import { bhkPathNumber } from '@/lib/seoLandingPath'

export const LAND_RE = /\b(land|plot|farm|agricultur)/i
export const COMMERCIAL_RE =
  /\b(office|shop|showroom|warehouse|godown|commercial|industrial|factory|cold storage|hotel|restaurant|banquet|marriage|conference|party)/i

/** Categories where a BHK path prefix makes sense (mirrors SeoLandingPayloadBuilder::isResidentialCategory). */
const RESIDENTIAL_BHK_RE = /^(?!.*(warehouse|weekend)).*(flat|apartment|house|villa|bungalow|tenement|penthouse|duplex)/i

export interface NavLink {
  label: string
  href: string
}

export function titleCase(value: string): string {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Lower-kebab slug for SEO path segments. */
export function toSeoSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Build listing hrefs as Phase-1 SEO pathnames only.
 * Extra filters (bhk, verified, commercial intent, …) stay as query on the SEO path.
 * Never returns `/properties?…` — without a city, returns bare `/properties`.
 */
export function buildPropertyHref(intent: string, params: Record<string, string | undefined>): string {
  const city = toSeoSlug(params.city ?? '')
  const categoryRaw = (params.category ?? '').trim()
  const areaRaw = (params.area ?? '').trim()
  // Always slugify for path segments (names with spaces → kebab).
  const categorySlug = toSeoSlug(params.categorySlug || categoryRaw)
  const areaIsId = /^\d+$/.test(areaRaw)
  const areaSlug = !areaIsId && areaRaw ? toSeoSlug(areaRaw) : ''

  const extras = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (!value) continue
    if (key === 'city' || key === 'category' || key === 'categorySlug' || key === 'area' || key === 'bhk') continue
    extras.set(key, value)
  }
  const bhkRaw = (params.bhk ?? '').trim()
  const bhkNum = bhkPathNumber(bhkRaw)

  const saleOrRent = intent === 'buy' ? 'sale' : intent === 'rent' ? 'rent' : null
  const isCommercial = intent === 'commercial'

  let path: string | null = null

  if (!city) {
    // No city → generic listing shell (no query-string city/intent URLs).
    if (bhkRaw) extras.set('bhk', bhkRaw)
    const qs = extras.toString()
    return qs ? `/properties?${qs}` : '/properties'
  }

  if (isCommercial) {
    // No Phase-1 commercial pathname — city hub + intent query.
    path = areaSlug ? `/properties-in-${areaSlug}-${city}` : `/properties-in-${city}`
    extras.set('intent', 'commercial')
    if (categoryRaw) extras.set('category', categoryRaw)
    if (areaIsId) extras.set('area', areaRaw)
  } else if (saleOrRent && categorySlug && areaSlug) {
    path = `/${categorySlug}-for-${saleOrRent}-in-${areaSlug}-${city}`
  } else if (saleOrRent && categorySlug && areaIsId) {
    path = `/${categorySlug}-for-${saleOrRent}-in-${city}`
    extras.set('area', areaRaw)
  } else if (saleOrRent && categorySlug && !areaRaw) {
    path = `/${categorySlug}-for-${saleOrRent}-in-${city}`
  } else if (saleOrRent && !categorySlug && !areaRaw) {
    path = `/property-for-${saleOrRent}-in-${city}`
  } else if (saleOrRent && !categorySlug && areaSlug) {
    path = `/properties-in-${areaSlug}-${city}`
    extras.set('intent', intent === 'rent' ? 'rent' : 'buy')
  } else if (saleOrRent && !categorySlug && areaIsId) {
    path = `/property-for-${saleOrRent}-in-${city}`
    extras.set('area', areaRaw)
  } else if (!saleOrRent && !categorySlug && !areaRaw) {
    path = `/properties-in-${city}`
  } else if (!saleOrRent && !categorySlug && areaSlug) {
    path = `/properties-in-${areaSlug}-${city}`
  } else if (!saleOrRent && !categorySlug && areaIsId) {
    path = `/properties-in-${city}`
    extras.set('area', areaRaw)
  } else if (!saleOrRent && categorySlug && !areaRaw) {
    path = `/${categorySlug}-in-${city}`
  } else if (!saleOrRent && categorySlug && areaSlug) {
    path = `/${categorySlug}-in-${areaSlug}-${city}`
  } else if (!saleOrRent && categorySlug && areaIsId) {
    path = `/${categorySlug}-in-${city}`
    extras.set('area', areaRaw)
  } else {
    // Catch-all: always land on city hub with remaining dims as query.
    path = `/properties-in-${city}`
    if (saleOrRent) extras.set('intent', intent === 'rent' ? 'rent' : 'buy')
    if (categoryRaw) extras.set('category', categoryRaw)
    if (areaRaw) extras.set('area', areaRaw)
  }

  // "{n}-bhk-" prefix on residential category and property-for-sale/rent paths (not on properties-in-* hubs).
  const bhkCategoryOk = !categorySlug || RESIDENTIAL_BHK_RE.test(categoryRaw || categorySlug)
  if (bhkNum && bhkCategoryOk && !isCommercial && !path.startsWith('/properties-in-')) {
    path = `/${bhkNum}-bhk-${path.slice(1)}`
  } else if (bhkRaw) {
    extras.set('bhk', bhkRaw)
  }

  const qs = extras.toString()
  return qs ? `${path}?${qs}` : path
}

export interface CategoryLike {
  name: string
  slug?: string | null
  count?: number
}

export function splitCategories(categories: CategoryLike[]) {
  const active = categories.filter((c) => (c.count ?? 0) > 0)
  const pool = active.length ? active : categories
  const byCount = [...pool].sort((a, b) => (b.count ?? 0) - (a.count ?? 0))

  return {
    land: byCount.filter((c) => LAND_RE.test(c.name)),
    commercial: byCount.filter((c) => !LAND_RE.test(c.name) && COMMERCIAL_RE.test(c.name)),
    residential: byCount.filter((c) => !LAND_RE.test(c.name) && !COMMERCIAL_RE.test(c.name)),
  }
}
